import { useState, useEffect, useRef, useCallback } from 'react';
import Highcharts from 'highcharts';
import 'highcharts/modules/stock';
import { callWidgetApi } from '../../../utils/widgetApi';
import { getChartTheme, getPriceColor } from '../chartTheme';

const SEARCH_BASE = import.meta.env.VITE_AUTH_BASE_URL || 'https://api2.simplize.vn';

async function searchTickers(query) {
    if (!query || query.length < 1) return [];
    const res = await fetch(`${SEARCH_BASE}/api/search/company/suggestions?q=${encodeURIComponent(query)}&t=&page=0&size=5`);
    const json = await res.json();
    return json?.data || [];
}

function TickerSearchDropdown({ onSelect, existingTickers = [], onClose }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const handleSearch = useCallback((q) => {
        setQuery(q);
        setActiveIdx(-1);
        clearTimeout(timerRef.current);
        if (!q.trim()) { setResults([]); return; }
        timerRef.current = setTimeout(async () => {
            setLoading(true);
            const data = await searchTickers(q);
            setResults(data.filter(d => !existingTickers.includes(d.ticker)));
            setLoading(false);
        }, 300);
    }, [existingTickers]);

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx(prev => Math.min(prev + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && activeIdx >= 0 && results[activeIdx]) {
            e.preventDefault();
            onSelect(results[activeIdx]);
            onClose();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    useEffect(() => {
        if (activeIdx >= 0 && listRef.current) {
            const el = listRef.current.children[activeIdx];
            if (el) el.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIdx]);

    return (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
            <div className="p-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 flex-shrink-0">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={e => handleSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Tìm kiếm mã..."
                        className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400"
                    />
                </div>
            </div>
            <div ref={listRef} className="max-h-[200px] overflow-y-auto">
                {loading && (
                    <div className="flex items-center justify-center py-4">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                    </div>
                )}
                {!loading && results.map((item, idx) => (
                    <button
                        key={item.ticker}
                        onClick={() => { onSelect(item); onClose(); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${
                            idx === activeIdx ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                    >
                        {item.imageUrl ? (
                            <img src={item.imageUrl} alt="" className="w-7 h-7 rounded-full object-contain bg-white flex-shrink-0" />
                        ) : (
                            <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {item.ticker?.charAt(0)}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{item.ticker}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.name}</div>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0">{item.stockExchange}</span>
                    </button>
                ))}
                {!loading && query && results.length === 0 && (
                    <div className="text-center py-4 text-xs text-gray-400">Không tìm thấy kết quả</div>
                )}
            </div>
        </div>
    );
}

const TIME_RANGES = {
    '1D': { days: 1, interval: '5m' },
    '5D': { days: 5, interval: '15m' },
    '1M': { days: 30, interval: '1h' },
    '3M': { days: 90, interval: '1d' },
    '1Y': { days: 365, interval: '1d' },
    '5Y': { days: 1825, interval: '1d' },
    'MAX': { days: null, interval: '1w' },
};

function formatPrice(value, decimals = 0) {
    if (value == null) return '-';
    return Number(value).toLocaleString('vi-VN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function formatNumber(value, options = {}) {
    if (value == null) return '-';
    const { decimals = 2, compact = false } = options;
    if (compact && Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (compact && Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (compact && Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return value.toLocaleString('vi-VN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function PriceChange({ change, change_percent }) {
    if (change == null && change_percent == null) return null;
    const isUp = (change || 0) > 0;
    const isDown = (change || 0) < 0;
    const color = isUp ? 'text-green-500' : isDown ? 'text-red-500' : 'text-gray-500 dark:text-gray-400';
    const sign = isUp ? '+' : '';
    const parts = [];
    if (change != null) parts.push(`${sign}${formatPrice(change)}`);
    if (change_percent != null) parts.push(`(${sign}${change_percent.toFixed(2)}%)`);
    return <span className={`text-sm font-medium ${color}`}>{parts.join(' ')}</span>;
}

function PriceChart({ ticker, type, timeRange, chartType }) {
    const chartRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [hasData, setHasData] = useState(false);
    const isDarkMode = document.documentElement.classList.contains('dark');

    useEffect(() => {
        if (!chartRef.current) return;
        let cancelled = false;
        setLoading(true);
        setHasData(false);

        const fetchAndRender = async () => {
            try {
                const config = TIME_RANGES[timeRange] || TIME_RANGES['1Y'];
                const now = Math.floor(Date.now() / 1000);
                let from;
                if (timeRange === 'YTD') {
                    from = Math.floor(Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000);
                } else if (config.days) {
                    from = now - (config.days * 86400);
                } else {
                    from = 946684800;
                }

                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers: [ticker],
                    interval: config.interval,
                    from_timestamp: from,
                    to_timestamp: now,
                    type: type || 'stock',
                });
                if (cancelled || !chartRef.current) return;

                const data = result?.data?.[ticker] || result?.[ticker];
                if (!Array.isArray(data) || data.length < 2) return;

                const theme = getChartTheme(isDarkMode);

                if (chartType === 'candlestick') {
                    renderCandlestick(chartRef.current, data, ticker, theme, isDarkMode);
                } else {
                    renderArea(chartRef.current, data, ticker, theme, isDarkMode);
                }
                if (!cancelled) setHasData(true);
            } catch {
                // silent
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchAndRender();
        return () => {
            cancelled = true;
            if (chartRef.current) {
                const chart = Highcharts.charts.find(c => c?.renderTo === chartRef.current);
                if (chart) chart.destroy();
            }
        };
    }, [ticker, type, timeRange, chartType, isDarkMode]);

    return (
        <div className="relative">
            {loading && (
                <div className="absolute inset-0 animate-pulse bg-gray-50 dark:bg-white/5 rounded z-10" />
            )}
            <div ref={chartRef} className="w-full" style={{ height: hasData || loading ? '250px' : '0px' }} />
        </div>
    );
}

function renderArea(container, data, ticker, theme, isDarkMode) {
    const chartData = data.map(d => {
        if (Array.isArray(d)) return [d[0] * 1000, d[4]];
        const ts = d.timestamp || d.ts || d.date;
        const price = d.priceClose || d.close || d.price;
        return [ts < 1e12 ? ts * 1000 : ts, price];
    }).filter(([t, p]) => t && p != null).sort((a, b) => a[0] - b[0]);

    if (chartData.length < 2) return;

    const lastPrice = chartData[chartData.length - 1][1];

    Highcharts.chart(container, {
        chart: {
            ...theme.chart,
            type: 'areaspline',
            height: 250,
            spacing: [10, 0, 10, 0],
            marginLeft: 0,
            marginRight: 0,
        },
        title: { text: null },
        credits: theme.credits,
        exporting: { enabled: false },
        legend: { enabled: false },
        xAxis: { ...theme.xAxis, type: 'datetime' },
        yAxis: {
            ...theme.yAxis,
            labels: {
                ...theme.yAxis.labels,
                align: 'left', x: 5, y: -2,
                style: { ...theme.yAxis.labels?.style, fontSize: '11px' },
                formatter: function () { return formatPrice(this.value); },
            },
            plotLines: [{
                value: lastPrice,
                color: isDarkMode ? 'rgba(156,163,175,0.3)' : 'rgba(156,163,175,0.4)',
                dashStyle: 'Dash', width: 1,
                label: {
                    text: formatPrice(lastPrice), align: 'right', x: -5,
                    style: { fontSize: '10px', color: isDarkMode ? '#9ca3af' : '#6b7280' },
                },
            }],
        },
        tooltip: {
            ...theme.tooltip,
            xDateFormat: '%d/%m/%Y',
            pointFormatter: function () {
                return `<span style="color:${this.series.color}">\u25CF</span> ${formatPrice(this.y)}<br/>`;
            },
        },
        plotOptions: {
            areaspline: {
                marker: { enabled: false },
                lineWidth: 2,
                states: { hover: { lineWidth: 2 } },
                threshold: lastPrice,
                color: '#22c55e',
                negativeColor: '#ef4444',
                fillColor: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [[0, '#22c55e40'], [1, '#22c55e00']],
                },
                negativeFillColor: {
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [[0, '#ef444400'], [1, '#ef444440']],
                },
            },
        },
        series: [{
            name: ticker,
            data: chartData,
        }],
    });
}

function renderCandlestick(container, data, ticker, theme, isDarkMode) {
    const ohlcData = [];
    const volumeData = [];

    data.forEach(item => {
        let ts, open, high, low, close, volume;
        if (Array.isArray(item)) {
            [ts, open, high, low, close, volume] = item;
        } else {
            ts = item.timestamp;
            open = item.open;
            high = item.high;
            low = item.low;
            close = item.close;
            volume = item.volume || item.totalVolume || 0;
        }
        const timestamp = ts < 1e12 ? ts * 1000 : ts;
        ohlcData.push([timestamp, open, high, low, close]);
        volumeData.push({ x: timestamp, y: volume || 0, color: getPriceColor(close >= open, 0.5) });
    });

    if (ohlcData.length < 2) return;

    const allPrices = ohlcData.flatMap(([, o, h, l, c]) => [o, h, l, c]).filter(Boolean);
    const dataMin = Math.min(...allPrices);
    const dataMax = Math.max(...allPrices);
    const padding = (dataMax - dataMin) * 0.02;

    Highcharts.chart(container, {
        chart: {
            ...theme.chart,
            height: 250,
            spacing: [10, 0, 10, 0],
            marginLeft: 0,
            marginRight: 0,
        },
        title: { text: null },
        credits: theme.credits,
        exporting: { enabled: false },
        legend: { enabled: false },
        xAxis: { ...theme.xAxis, type: 'datetime', ordinal: true },
        yAxis: [{
            ...theme.yAxis,
            height: '75%',
            min: dataMin - padding,
            max: dataMax + padding,
            startOnTick: false,
            endOnTick: false,
            labels: {
                ...theme.yAxis.labels,
                align: 'left', x: 5, y: -2,
                style: { ...theme.yAxis.labels?.style, fontSize: '11px' },
                formatter: function () { return formatPrice(this.value); },
            },
        }, {
            title: { text: null },
            top: '78%', height: '22%', offset: 0,
            gridLineWidth: 0, labels: { enabled: false },
        }],
        tooltip: {
            ...theme.tooltip,
            shared: true,
            formatter: function () {
                const textColor = isDarkMode ? 'rgba(229,229,229,0.9)' : 'rgba(51,51,51,0.9)';
                let html = `<div style="font-size:11px;margin-bottom:5px;color:${textColor}">${Highcharts.dateFormat('%d/%m/%Y', this.x)}</div>`;
                this.points.forEach(point => {
                    if (point.series.type === 'candlestick') {
                        html += `<div style="padding:2px 0;color:${textColor}"><b>O:</b> ${formatNumber(point.point.open, { decimals: 0 })} <b>H:</b> ${formatNumber(point.point.high, { decimals: 0 })} <b>L:</b> ${formatNumber(point.point.low, { decimals: 0 })} <b>C:</b> ${formatNumber(point.point.close, { decimals: 0 })}</div>`;
                    } else if (point.series.type === 'column') {
                        html += `<div style="padding:2px 0;color:${textColor}"><b>KL:</b> ${formatNumber(point.y, { compact: true })}</div>`;
                    }
                });
                return html;
            },
        },
        plotOptions: { ...theme.plotOptions },
        series: [
            { type: 'candlestick', name: ticker, data: ohlcData, yAxis: 0 },
            { type: 'column', name: 'Volume', data: volumeData, yAxis: 1 },
        ],
    });
}

const toolbarBtnClass = "flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors";

function TimeRangeSelect({ value, onChange }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button onClick={() => setOpen(p => !p)} className={toolbarBtnClass}>
                {value}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-400">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1 min-w-[60px]">
                        {Object.keys(TIME_RANGES).map(range => (
                            <button
                                key={range}
                                onClick={() => { onChange(range); setOpen(false); }}
                                className={`w-full px-3 py-1.5 text-xs font-medium text-left transition-colors ${
                                    value === range
                                        ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function CompareButton({ onAdd, existingTickers }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button onClick={() => setOpen(p => !p)} className={toolbarBtnClass}>
                So sánh
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-400">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <TickerSearchDropdown
                        existingTickers={existingTickers}
                        onSelect={onAdd}
                        onClose={() => setOpen(false)}
                    />
                </>
            )}
        </div>
    );
}

function ChartToolbar({ timeRange, onTimeRangeChange, chartType, onChartTypeChange, onAddCompare, existingTickers }) {
    return (
        <div className="flex items-center gap-2 py-2">
            <TimeRangeSelect value={timeRange} onChange={onTimeRangeChange} />
            <div className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <button
                    onClick={() => onChartTypeChange('area')}
                    className={`px-2 py-1 transition-colors ${chartType === 'area' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    title="Line chart"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                </button>
                <button
                    onClick={() => onChartTypeChange('candlestick')}
                    className={`px-2 py-1 transition-colors ${chartType === 'candlestick' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                    title="Candlestick chart"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6m0 1a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z M6 4l0 2 M6 11l0 9 M10 14m0 1a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z M12 4l0 10 M12 19l0 1 M16 5m0 1a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z M18 4l0 1 M18 11l0 9" />
                    </svg>
                </button>
            </div>
            {onAddCompare && (
                <CompareButton onAdd={onAddCompare} existingTickers={existingTickers} />
            )}
        </div>
    );
}

function TickerCard({ item, type, onAddCompare }) {
    const [timeRange, setTimeRange] = useState('1Y');
    const [chartType, setChartType] = useState('area');
    const priceDecimals = type === 'index' ? 2 : 0;

    return (
        <>
            <div className="pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-gray-900 dark:text-white">{item.ticker}</span>
                            {item.exchange && (
                                <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-300">
                                    {item.exchange}
                                </span>
                            )}
                        </div>
                        {item.name && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{item.name}</p>
                        )}
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {formatPrice(item.price, priceDecimals)}
                        </div>
                        <div className="text-sm">
                            <PriceChange change={item.change} change_percent={item.change_percent} />
                        </div>
                    </div>
                </div>
            </div>

            <ChartToolbar
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                chartType={chartType}
                onChartTypeChange={setChartType}
                onAddCompare={onAddCompare}
                existingTickers={[item.ticker]}
            />

            <PriceChart ticker={item.ticker} type={type} timeRange={timeRange} chartType={chartType} />

            {item.metrics && item.metrics.length > 0 && (
                <div className="py-3 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex">
                        {chunkArray(item.metrics, 3).map((col, ci, arr) => (
                            <div
                                key={ci}
                                className={`flex-1 space-y-2 ${ci === 0 ? 'pr-6' : ''} ${ci > 0 && ci < arr.length - 1 ? 'px-6 border-r border-gray-200 dark:border-gray-700' : ''} ${ci > 0 && ci === arr.length - 1 ? 'pl-6' : ''} ${ci === 0 && arr.length > 1 ? 'border-r border-gray-200 dark:border-gray-700' : ''}`}
                            >
                                {col.map((m, mi) => (
                                    <div key={mi} className="flex items-center justify-between">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">{m.label}</span>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{m.value}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

const COMPARE_COLORS = ['#3B82F6', '#F59E0B', '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2'];

function CompareChart({ tickers, type, timeRange, onHover, onPriceData }) {
    const chartRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [hasData, setHasData] = useState(false);
    const isDarkMode = document.documentElement.classList.contains('dark');

    useEffect(() => {
        if (!chartRef.current) return;
        let cancelled = false;
        setLoading(true);
        setHasData(false);

        const fetchAll = async () => {
            try {
                const config = TIME_RANGES[timeRange] || TIME_RANGES['1Y'];
                const now = Math.floor(Date.now() / 1000);
                let from;
                if (timeRange === 'YTD') {
                    from = Math.floor(Date.UTC(new Date().getUTCFullYear(), 0, 1) / 1000);
                } else if (config.days) {
                    from = now - (config.days * 86400);
                } else {
                    from = 946684800;
                }

                const result = await callWidgetApi('get_historical_prices_range', {
                    tickers,
                    interval: config.interval,
                    from_timestamp: from,
                    to_timestamp: now,
                    type: type || 'stock',
                });
                if (cancelled || !chartRef.current) return;

                const theme = getChartTheme(isDarkMode);
                const series = tickers.map((ticker, idx) => {
                    const data = result?.data?.[ticker] || [];
                    if (!Array.isArray(data) || data.length < 2) return null;

                    const prices = data.map(d => {
                        if (Array.isArray(d)) return [d[0] * 1000, d[4]];
                        const ts = d.timestamp || d.ts;
                        const price = d.priceClose || d.close || d.price;
                        return [ts < 1e12 ? ts * 1000 : ts, price];
                    }).filter(([t, p]) => t && p != null).sort((a, b) => a[0] - b[0]);

                    if (prices.length < 2) return null;
                    const base = prices[0][1];
                    const normalized = prices.map(([t, p]) => [t, ((p - base) / base) * 100]);

                    return {
                        name: ticker,
                        data: normalized,
                        color: COMPARE_COLORS[idx % COMPARE_COLORS.length],
                        lineWidth: 2,
                    };
                }).filter(Boolean);

                if (series.length === 0) return;

                const priceMap = {};
                tickers.forEach(ticker => {
                    const data = result?.data?.[ticker] || [];
                    if (!Array.isArray(data) || data.length < 2) return;
                    const first = data[0];
                    const last = data[data.length - 1];
                    const close = Array.isArray(last) ? last[4] : (last.priceClose || last.close);
                    const basePrice = Array.isArray(first) ? first[4] : (first.priceClose || first.close);
                    if (close != null && basePrice != null) {
                        const change = close - basePrice;
                        const changePct = basePrice ? (change / basePrice) * 100 : 0;
                        priceMap[ticker] = { price: close, change, change_percent: changePct };
                    }
                });
                onPriceData?.(priceMap);

                Highcharts.chart(chartRef.current, {
                    chart: {
                        ...theme.chart,
                        type: 'line',
                        height: 250,
                        spacing: [10, 0, 10, 0],
                        marginLeft: 0,
                        marginRight: 0,
                    },
                    title: { text: null },
                    credits: theme.credits,
                    exporting: { enabled: false },
                    legend: { enabled: false },
                    xAxis: { ...theme.xAxis, type: 'datetime' },
                    yAxis: {
                        ...theme.yAxis,
                        labels: {
                            ...theme.yAxis.labels,
                            align: 'left', x: 5, y: -2,
                            style: { ...theme.yAxis.labels?.style, fontSize: '11px' },
                            format: '{value}%',
                        },
                        plotLines: [{
                            value: 0,
                            color: isDarkMode ? '#6b7280' : '#9ca3af',
                            dashStyle: 'Dash',
                            width: 1,
                        }],
                    },
                    tooltip: {
                        ...theme.tooltip,
                        shared: true,
                        xDateFormat: '%d/%m/%Y',
                        pointFormat: '<span style="color:{series.color}">\u25CF {series.name}</span>: <b>{point.y:.2f}%</b><br/>',
                    },
                    plotOptions: {
                        line: {
                            marker: { enabled: false },
                            states: { hover: { lineWidth: 3 } },
                        },
                        series: {
                            point: {
                                events: {
                                    mouseOver: function () {
                                        const chart = this.series.chart;
                                        const hoverMap = {};
                                        chart.series.forEach(s => {
                                            const pts = s.points;
                                            const closest = pts.reduce((prev, curr) =>
                                                Math.abs(curr.x - this.x) < Math.abs(prev.x - this.x) ? curr : prev
                                            );
                                            hoverMap[s.name] = { pct: closest.y };
                                        });
                                        onHover?.(hoverMap);
                                    },
                                },
                            },
                            events: {
                                mouseOut: function () { onHover?.(null); },
                            },
                        },
                    },
                    series,
                });
                if (!cancelled) setHasData(true);
            } catch {
                // silent
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchAll();
        return () => {
            cancelled = true;
            if (chartRef.current) {
                const chart = Highcharts.charts.find(c => c?.renderTo === chartRef.current);
                if (chart) chart.destroy();
            }
        };
    }, [tickers.join(','), type, timeRange, isDarkMode]);

    return (
        <div className="relative">
            {loading && <div className="absolute inset-0 animate-pulse bg-gray-50 dark:bg-white/5 rounded z-10" />}
            <div ref={chartRef} className="w-full" style={{ height: hasData || loading ? '250px' : '0px' }} />
        </div>
    );
}

function CompareStockRow({ item, color, hoverPct, onRemove }) {
    const hasPrice = item.price != null;
    const displayPct = hoverPct != null ? hoverPct : (item.change_percent || 0);
    const displayChange = hoverPct != null && hasPrice ? (item.price * hoverPct / 100) : (item.change || 0);
    const displayPrice = hoverPct != null && hasPrice ? (item.price * (1 + hoverPct / 100)) : item.price;
    const isUp = displayPct > 0;
    const isDown = displayPct < 0;

    return (
        <div className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
            <div className="w-1 h-8 rounded flex-shrink-0" style={{ backgroundColor: color }} />
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 dark:text-white">{item.ticker}</div>
                {item.name && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.name}</div>}
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white text-right min-w-[80px]">{formatPrice(displayPrice)}</div>
            <div className={`text-sm text-right min-w-[75px] ${isUp ? 'text-green-600' : isDown ? 'text-red-600' : 'text-gray-500'}`}>
                {isUp ? '+' : ''}{formatPrice(displayChange)}
            </div>
            <div className={`text-xs font-medium px-2 py-0.5 rounded text-right whitespace-nowrap ${
                isUp ? 'text-green-700 bg-green-50 dark:text-green-400 dark:bg-green-900/30'
                    : isDown ? 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
                        : 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
            }`}>
                {isUp ? '\u2197' : isDown ? '\u2198' : ''} {Math.abs(displayPct).toFixed(2)}%
            </div>
            {onRemove && (
                <button onClick={onRemove} className="text-gray-300 hover:text-gray-500 flex-shrink-0 ml-1">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                </button>
            )}
        </div>
    );
}

function CompareView({ items: initialItems, type, onSwitchToSingle, onAddCompare }) {
    const [items, setItems] = useState(initialItems);
    const [timeRange, setTimeRange] = useState('1Y');
    const [hoverData, setHoverData] = useState(null);
    const [priceData, setPriceData] = useState({});
    const tickers = items.map(i => i.ticker);

    const enrichedItems = items.map(item => ({
        ...item,
        price: item.price ?? priceData[item.ticker]?.price,
        change: item.change ?? priceData[item.ticker]?.change,
        change_percent: item.change_percent ?? priceData[item.ticker]?.change_percent,
    }));

    const handleRemove = (index) => {
        const next = items.filter((_, i) => i !== index);
        if (next.length <= 1) {
            onSwitchToSingle?.(next);
            return;
        }
        setItems(next);
    };

    return (
        <>
            <div className="pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-4 flex-wrap">
                    {items.map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COMPARE_COLORS[i % COMPARE_COLORS.length] }} />
                            <span className="font-bold text-sm text-gray-900 dark:text-white">{item.ticker}</span>
                            {item.exchange && (
                                <span className="text-xs text-gray-500">{item.exchange}</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-2 py-2">
                <TimeRangeSelect value={timeRange} onChange={setTimeRange} />
                <CompareButton onAdd={(result) => {
                    setItems(prev => [...prev, { ticker: result.ticker, name: result.name, exchange: result.stockExchange }]);
                }} existingTickers={tickers} />
            </div>

            <CompareChart tickers={tickers} type={type} timeRange={timeRange} onHover={setHoverData} onPriceData={setPriceData} />

            <div className="mt-2 max-h-[220px] overflow-y-auto">
                {enrichedItems.map((item, i) => (
                    <CompareStockRow
                        key={i}
                        item={item}
                        color={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                        hoverPct={hoverData?.[item.ticker]?.pct}
                        onRemove={() => handleRemove(i)}
                    />
                ))}
            </div>
        </>
    );
}

function chunkArray(arr, cols) {
    const perCol = Math.ceil(arr.length / cols);
    const result = [];
    for (let i = 0; i < cols; i++) {
        result.push(arr.slice(i * perCol, (i + 1) * perCol));
    }
    return result.filter(c => c.length > 0);
}

export default function AssetCard({ items: initialItems, type = 'stock' }) {
    const [items, setItems] = useState(initialItems);

    if (!items || items.length === 0) return null;

    const handleAddCompare = (searchResult) => {
        const newItem = {
            ticker: searchResult.ticker,
            name: searchResult.name,
            exchange: searchResult.stockExchange,
        };
        setItems(prev => [...prev, newItem]);
    };

    if (items.length > 1) {
        return (
            <CompareView
                items={items}
                type={type}
                onSwitchToSingle={(remaining) => setItems(remaining)}
                onAddCompare={handleAddCompare}
            />
        );
    }

    return <TickerCard item={items[0]} type={type} onAddCompare={handleAddCompare} />;
}
